import { makeStyles, Tab, Tabs } from "@material-ui/core";
import {
    attributes,
    CollectionConstructor, Linked,
    subsetOf,
    type,
    Events
} from "@type-r/models";
import { useIO, useModel, useEvent } from '@type-r/react';
import * as React from "react";
import { InvoiceModel, InvoicesTotals } from "api/invoice";
import { RemittanceAdviceSummary } from "../inmatch/api";
import { categories, CategoryTab } from "./common";

export const SearchCategories = attributes({
    status: {
        options: [{ label: "Select All", value: "All" }]
    },

    customer: {
        options: [{ label: "Select All", value: "All" }]
    },

    supplier: {
        options: [{ label: "Select All", value: "All" }]
    },

    date: {
        options: [
            { label: "Invoice Due Date", value: "invoiceDueDateUtc" },
            { label: "Payment Completion Date", value: "paymentCompletionDateUtc" },
            { label: "Promise To Pay Date", value: "anticipatedPaymentDateUtc" }
        ]
    }
});

export type SearchCategories = InstanceType<typeof SearchCategories>;

export const InvoicesState = attributes({
    selected: subsetOf<CollectionConstructor<InvoiceModel>, InvoiceModel>(
        "invoices",
        InvoiceModel.Collection as any /* TODO - fix it */
    ),
    myActionItems: false,
    expandedAppBar: false,
    searchCategories: type(SearchCategories).null,
    advancedSearchFilter: "",
    promiseToPayDateSelectedInvoiceId: type(String).null
});

export type InvoicesState = InstanceType<typeof InvoicesState>;

export interface InvoiceHeaderProps {
    $category: Linked<CategoryTab>;
    state: InvoicesState;
}




const GetCategoryName = categoryName => {
    switch (categoryName) {
        case "disputed":
            return "Disputed";

        case "pending":
            return "Open Invoices";

        case "paymentinprogress":
            return "Payment In Progress";

        case "complete":
            return "Complete";

        default:
            return "All";
    }
};

export const InvoiceHeader = ({ $category, state }: InvoiceHeaderProps) => {
    const classes = useStyles();
    const totals = useModel(InvoicesTotals);
    const raTotals = useModel(RemittanceAdviceSummary);

    const isReady = useIO(
        async () =>
            Promise.all([
                totals.fetch(),
                raTotals.fetch(),
            ])
    );

    useEvent( Events, "totals:refresh", 
        () => {
            totals.fetch();
            raTotals.fetch();
        }
    );

    const onTabChange = changedCategory => {
        state.transaction(() => {
            if (changedCategory !== "check-box-tab") {
                state.expandedAppBar = false;
                state.advancedSearchFilter = null;

                $category.set( changedCategory );
            } 
            else {
                if( state.myActionItems ) {
                    $category.set( "all" );
                }
            }

            state.promiseToPayDateSelectedInvoiceId = null;
        });
    };

    // refresh the invoice management summary every 60 sec;
    const REFRESH_SUMMARY_INTERVAL_MS = 60000;

    React.useEffect(() => {
        const refreshInterval = setInterval(
            () => {
                Events.trigger("totals:refresh");
            },
            REFRESH_SUMMARY_INTERVAL_MS
        );

        return () => {
            clearInterval(refreshInterval);
        };
    }, []);

    return isReady ? (
        <div className={classes.root}>
            <Tabs value={$category.value} onChange={(_, x) => onTabChange(x)}>
                {categories.map(category => (
                    <Tab
                        value={category}
                        key={category}
                        classes={{
                            root: classes.tab,
                            selected: classes.selected
                        }}
                        disabled={!totals[category]}
                        label={
                            <CategoryTab
                                key={category}
                                category={GetCategoryName(category)}
                                count={totals[category] || 0}
                            />
                        }
                    />
                ))}

                <Tab
                    key="disabled"
                    value={"separator"}
                    disabled
                    classes={{
                        root: classes.separator
                    }}
                    label={<span className={classes.separatorElement} />}
                />

                <Tab
                    classes={{
                        root: classes.tab,
                        selected: classes.selected
                    }}
                    disabled={false}
                    value="remittance-advices"
                    label={
                        <CategoryTab key={"remittance"} category={"remittance"} count={raTotals.count} />
                    }
                />

                <Tab 
                    classes={{
                        root: classes.tab,
                        selected: classes.selected
                    }}

                    value="statement"
                    label={<>statement<br/>of<br/>account</>}
                />
            </Tabs>
        </div>
    ) : (
        <></>
    );
};

const CategoryTab = ({ category, count }) => {
    const classes = useStyles();

    return (
        <div className={classes.orderTab}>
            <div className={classes.number}>{count.toLocaleString()}</div>
            <div className={classes.title}>{category} </div>
        </div>
    );
};

const useStyles = makeStyles({
    root: {
        backgroundColor: "#052C4C",
        color: "white",
        paddingBottom: 10
    },

    orderTab: {
        fontFamily: "Montserrat",
        paddingBottom: 2
    },

    separator: {
        width: 35,
        minWidth: 20
    },

    separatorElement: {
        borderLeft: "solid",
        borderWidth: 1,
        borderColor: "white",
        height: 41,
        marginTop: 7
    },

    number: {
        fontSize: 28,
        fontWeight: 300
    },

    title: {
        fontSize: 12,
        fontWeight: 600,
        marginTop: -8,
        textTransform: "uppercase"
    },

    tab: {
        minWidth: 80, // a number of your choice
        // width: 80, // a number of your choice
        textAlign: "left",

        "&:hover": {
            color: "#FFF",
            opacity: 1
        },

        "&:focus": {
            color: "#FFF",
            opacity: 1
        },

        "&$selected": {
            color: "#FFF",
            opacity: 1
        }
    },

    selected: {}
});
